import { useEffect, useState } from "react";

import {
  useDeleteSelectedCareer,
  useSelectedCareers,
} from "@features/career-compare/api";
import { useDeleteCareerMatches } from "@features/career-matches/api/use-delete-career-matches";
import { useDeleteCareerPlan } from "@features/goals/api/use-delete-career-plan";
import { useDeleteJourneyCareer } from "@features/goals/api/use-delete-journey-career";
import { useCreateInterestInventory } from "@features/interest-inventory/api/use-create-interest-inventory";
import { useDeleteUserPreliminaryCareers } from "@features/interest-inventory/api/use-delete-user-preliminary-careers";
import { useLocalStorage } from "@hooks/use-local-storage";

export const useStartOver = () => {
  const [isStartingOver, setIsStartingOver] = useState(false);
  const [_, setViewedIntros] = useLocalStorage("viewedIntros");

  const {
    mutateAsync: createInterestInventory,
    isPending: isCreatingInterestInventory,
  } = useCreateInterestInventory();
  const { mutateAsync: deleteCareerPlan, isPending: isDeletingCareerPlan } =
    useDeleteCareerPlan();
  const {
    mutateAsync: deleteJourneyCareer,
    isPending: isDeletingJourneyCareer,
  } = useDeleteJourneyCareer();
  const { mutateAsync: deleteCareerMatches } = useDeleteCareerMatches();

  const {
    mutateAsync: deletePreliminaryCareers,
    isPending: isDeletingPreliminaryCareers,
  } = useDeleteUserPreliminaryCareers();

  const { data: selectedCareers } = useSelectedCareers();
  const {
    mutateAsync: deleteSelectedCareers,
    isPending: isDeletingSelectedCareers,
  } = useDeleteSelectedCareer();

  useEffect(() => {
    setIsStartingOver(
      isCreatingInterestInventory ||
        isDeletingCareerPlan ||
        isDeletingJourneyCareer ||
        isDeletingPreliminaryCareers ||
        isDeletingSelectedCareers,
    );
  }, [
    isCreatingInterestInventory,
    isDeletingCareerPlan,
    isDeletingJourneyCareer,
    isDeletingPreliminaryCareers,
    isDeletingSelectedCareers,
  ]);

  return {
    isStartingOver,
    startOver: async ({ restartInterestInventory } = {}) => {
      // all start over actions need to delete any existing career plan and journey
      // career. note that budget plans are handled on the BE.
      const promises = [deleteCareerPlan(), deleteJourneyCareer()];

      // reset intros for all but the first map -- when doing a complete start
      // over we automatically open the interest inventory so even then there's
      // no need to display the intro text
      setViewedIntros({ map1: true });

      if (restartInterestInventory) {
        // if the interest inventory is being restarted, we need to create a new
        // interest inventory entry and delete any career matches -- deleting career
        // matches will also delete any career ratings
        // and delete any selected careers
        const selectedCareersIds = selectedCareers?.map(({ id }) => id);
        promises.push(
          createInterestInventory(),
          deleteCareerMatches(),
          deletePreliminaryCareers(),
          deleteSelectedCareers({ career_ids: selectedCareersIds }),
        );
      }

      await Promise.allSettled(promises);
    },
  };
};
